// src/lib/topic-leverage.ts
// "Leverage" = how many test-day points a user can realistically gain
// from improving a given topic. Used to rank topics in the study plan
// so we recommend high-impact topics first instead of just the lowest
// raw accuracy.
//
// Why this matters:
//   Pure weakness ranking puts a 30%-accuracy obscure topic ahead of
//   a 60%-accuracy domain that has 4x more questions on test day.
//   The student spends a week on the obscure topic and gains 1 point.
//   Leverage ranking flips that.
//
// Formula:
//   leverage = max(0, ceiling - current_accuracy)
//              × questions_per_test_for_this_topic
//              × confidence_multiplier
//
//   - ceiling: practical mastery target (default 85%) — we don't assume
//     anyone reliably hits 100%, and aiming there inflates leverage for
//     near-mastered topics.
//   - questions_per_test: pulled from sat-domains.ts. This is the PER-TOPIC
//     question share (the domain's per-test count split evenly across its
//     topics), NOT the whole-domain count — crediting every topic with its
//     full domain count assumed mastering one topic captured all its
//     siblings' questions (1:1 transfer). See questionsPerTestForTopic.
//   - confidence_multiplier: ramps from 0.5 to 1.0 based on how many
//     questions the user has actually attempted in this topic. A 30%
//     accuracy from 2 questions is much less reliable than from 20,
//     so we down-weight low-sample topics.

import {
  questionsPerTestForTopic,
  domainForTopic,
  DOMAIN_QUESTIONS_PER_TEST,
} from '@/lib/test-engine/sat-domains'
import type { WeakTopic } from '@/lib/study-plan/study-plan'

/**
 * Practical mastery ceiling we target for each topic. 85% is roughly
 * "consistently get the easy and medium questions, occasionally miss a
 * hard one" — what an actual high-scoring SAT student looks like, not
 * a theoretical perfectionist.
 */
export const DEFAULT_CEILING_PCT = 85

/**
 * Below this many attempted questions, the accuracy estimate is shaky
 * and we discount the leverage score. This prevents a topic with 1/2
 * correct (50%, low confidence) from ranking ahead of a topic with
 * 18/30 correct (60%, high confidence).
 */
export const LOW_CONFIDENCE_CUTOFF = 5

/**
 * Above this many attempted questions, we trust the accuracy estimate
 * fully (multiplier = 1.0). Linear ramp between cutoff and full.
 */
export const FULL_CONFIDENCE_CUTOFF = 15

export type LeverageBreakdown = {
  /** The original WeakTopic record. */
  topic: WeakTopic
  /** Final leverage score — higher = more impactful to study. */
  leverage: number
  /**
   * False when the student has never answered a question in this topic, so
   * there is nothing to be weak AT. Such a topic scores zero leverage and must
   * be reported as unsampled rather than ranked — see NO DIAGNOSIS WITHOUT
   * EVIDENCE below.
   */
  hasEvidence: boolean
  /** Accuracy gap component: max(0, ceiling - pct). */
  gap: number
  /**
   * Per-topic frequency component: the topic's domain per-test question
   * count split evenly across the domain's topics. 0 if unmapped.
   */
  questionsPerTest: number
  /** Confidence component (0.5 to 1.0). */
  confidence: number
}

/**
 * Score a single topic on the leverage rubric. Exposed for unit tests
 * and for the "why this card?" rationale tooltip in the study plan UI.
 */
export function leverageScore(
  topic: WeakTopic,
  ceiling: number = DEFAULT_CEILING_PCT,
): LeverageBreakdown {
  // NO DIAGNOSIS WITHOUT EVIDENCE.
  //
  // A topic the student has never answered arrives here as pct 0, total 0 —
  // and pct 0 is the WIDEST possible gap, so the formula scored it as the
  // single most valuable thing they could study. Measured on the real
  // weights: an untouched data-analysis topic scored 54.6 against 38.6 for a
  // topic genuinely sitting at 55% over twenty attempts. The untouched topic
  // won, and the rationale under it read "You're at 0% here … only 0 attempts
  // so far" — a weakness invented out of the absence of a measurement.
  //
  // This is not hypothetical shape. mergeTopicMastery seeds EVERY topic in the
  // bank at 0/0 so the Mastery Map can draw empty cells, and that array is one
  // careless call away from this function. Three separate call sites already
  // guard the same case by hand — the mobile snapshot skips total <= 0, the
  // Mastery Map sorts untouched topics to the end, the domain breakdown
  // reports a null accuracy — which is three chances to forget it. The rule
  // belongs where the ranking happens, so no caller can get it wrong.
  //
  // It matters most for exactly the case that prompted it: the benchmark is
  // adaptive, so a student sits 44 of its 88 questions and some skills are
  // never put to them at all. "We did not ask" must never be reported as
  // "you cannot do this".
  const hasEvidence = topic.total > 0
  const gap = Math.max(0, ceiling - topic.pct)
  const questionsPerTest = questionsPerTestForTopic(topic.topic)
  const confidence = confidenceMultiplier(topic.total)
  const leverage = hasEvidence ? gap * questionsPerTest * confidence : 0

  return {
    topic,
    leverage,
    hasEvidence,
    gap,
    questionsPerTest,
    confidence,
  }
}

/**
 * Sort an array of WeakTopic descending by leverage score. Returns a new
 * array; original is untouched. Equal-leverage topics fall back to lower
 * pct first (still "weakest first" within a leverage tier) so the order
 * is stable across renders.
 *
 * Topics with leverage = 0 (unmapped domain or already at/above ceiling)
 * sink to the bottom — but they're still included so the caller can
 * choose whether to filter them out.
 */
export function rankTopicsByLeverage(
  topics: WeakTopic[],
  ceiling: number = DEFAULT_CEILING_PCT,
): LeverageBreakdown[] {
  return topics
    .map((t) => leverageScore(t, ceiling))
    .sort((a, b) => {
      if (b.leverage !== a.leverage) return b.leverage - a.leverage
      // Both scored zero: a MEASURED topic still outranks an unsampled one.
      // Without this the pct tie-breaker below puts the unsampled topic first,
      // because its pct is 0 — the very number that means "no data".
      if (a.hasEvidence !== b.hasEvidence) return a.hasEvidence ? -1 : 1
      // Tie-breaker: weaker topic first (smaller pct).
      return a.topic.pct - b.topic.pct
    })
}

/**
 * Linear ramp from 0.5 (very few questions attempted) to 1.0 (plenty).
 * Below LOW_CONFIDENCE_CUTOFF: clamped to 0.5.
 * Above FULL_CONFIDENCE_CUTOFF: clamped to 1.0.
 * In between: linear interpolation.
 */
export function confidenceMultiplier(totalAttempted: number): number {
  if (totalAttempted <= LOW_CONFIDENCE_CUTOFF) return 0.5
  if (totalAttempted >= FULL_CONFIDENCE_CUTOFF) return 1.0
  const range = FULL_CONFIDENCE_CUTOFF - LOW_CONFIDENCE_CUTOFF
  const offset = totalAttempted - LOW_CONFIDENCE_CUTOFF
  return 0.5 + (offset / range) * 0.5
}

/**
 * Generate a one-line, human-readable rationale for why this topic
 * was prioritized. Used by the "Why this card?" tooltip on the study
 * plan page.
 *
 * The phrasing is chosen to be honest and specific: it tells the user
 * what data drove the recommendation rather than hand-waving.
 */
/**
 * Student-facing "why this card?" sentence. Leads with what the student cares
 * about — their accuracy + how heavy the area is — instead of the internal
 * even-split mechanics. Stays QUALITATIVE about the payoff: a per-topic point
 * number would over-credit one topic with its whole domain's swing and read as
 * additive across rows when it isn't. The honest score figure is a single
 * board-level "bring these to target ≈ +M pts" (see leverage-board.ts).
 */
export function leverageRationale(b: LeverageBreakdown): string {
  // Said before anything else: with no attempts there is no accuracy to quote,
  // and the sentence below would read "You're at 0% here … only 0 attempts so
  // far" — which states a score and then admits there is no score.
  if (!b.hasEvidence) {
    return `You haven't answered a question in "${b.topic.topic}" yet, so there's nothing to measure here. Practise a few and we'll be able to tell you where you stand.`
  }

  if (b.questionsPerTest === 0) {
    return `You're at ${b.topic.pct}% on "${b.topic.topic}", but we don't have a frequency weight for it yet, so we can't size the payoff.`
  }

  if (b.gap === 0) {
    return `You're already at the ${DEFAULT_CEILING_PCT}% mastery target here — this is maintenance, not catch-up.`
  }

  const domain = domainForTopic(b.topic.topic)
  const domainTotal = DOMAIN_QUESTIONS_PER_TEST[domain]
  const sampleNote =
    b.confidence < 1.0 ? ` Still an early read — only ${b.topic.total} attempts so far.` : ''

  return `You're at ${b.topic.pct}% here, and ${domain} shows up a lot — about ${domainTotal} questions a test. Getting to ${DEFAULT_CEILING_PCT}% is one of your biggest point opportunities.${sampleNote}`
}
